import React, { useEffect, useState } from 'react';
import {
  displayStatus,
  evaluationPercentage,
  isFinding,
  percentageLabel,
} from '../../lib/evaluationIntegrity';
import { IntegrityFields, ScoreSummary } from '../../components/EvaluationIntegrity';
import { formatAnswerForPDF, isCriticalFinding } from '../../lib/answerFormat';
import { useParams, useNavigate } from 'react-router-dom';
import { evaluationsAPI } from '../../api/client';
import { getEvalSections } from '../../lib/evalSections';
import SemaforoBadge from '../../components/SemaforoBadge';
import StatusBadge from '../../components/StatusBadge';
import { ArrowLeft, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import ReportDownload from '../../components/ReportDownload';
import { getSemaforoBarColor } from '../../lib/criteria';

export function ScoreBar({ score, max, percentage }) {
  const pct = percentage ?? 0;
  // #Fase0-B4 — corte único del semáforo (C1), antes ≥81/≥61/≥41.
  const color = percentage == null ? '#94a3b8' : getSemaforoBarColor(pct);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 bg-slate-100 rounded-full h-3">
        <div className="h-3 rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-sm font-bold font-mono" style={{ color }}>{score}/{max}</span>
    </div>
  );
}

function CriterionRow({ criterion, item }) {
  const [expanded, setExpanded] = useState(false);
  const score = item?.score ?? 0;
  // #Fase0 — contrato C4: sin contestar no bloquea (igual que la API).
  const isBlocked = isCriticalFinding(criterion, item);
  const hasRecommendation =
    item?.recommendation || item?.auto_recommendation || criterion.recommendations?.[score];
  
  const scoreColor = score === 10 ? 'text-blue-700' :
                     score >= 8 ? 'text-green-700' : 
                     score >= 4 ? 'text-yellow-700' : 
                     'text-red-700';
  
  const scoreBg = score === 10 ? 'bg-blue-100' :
                  score >= 8 ? 'bg-green-100' : 
                  score >= 4 ? 'bg-yellow-100' : 
                  'bg-red-100';

  return (
    <div className={`border-b border-slate-100 ${isFinding(criterion, item) ? 'bg-red-50' : ''}`}>
      <div className="px-5">
        <IntegrityFields criterion={criterion} item={item} clientView />
      </div>
      {/* Header row - clickable */}
      <div 
        className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-slate-50 transition-colors" 
        onClick={() => setExpanded(!expanded)}
        data-testid={`criterion-client-${criterion.id}`}
      >
        <span className="w-8 h-8 flex items-center justify-center text-xs font-bold rounded bg-soem-navy text-white flex-shrink-0">
          {criterion.id}
        </span>
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-soem-navy text-sm block truncate">{criterion.title}</span>
          {!expanded && criterion.focus && (
            <span className="text-xs text-slate-500 block truncate">{criterion.focus}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {criterion.blocker && (
            <span className="bg-red-100 text-red-700 text-xs px-1.5 py-0.5 rounded font-bold hidden sm:inline">B</span>
          )}
          <span className={`font-black text-base px-2 py-0.5 rounded ${
            !isFinding(criterion, item) ? 'bg-slate-100 text-slate-600' : `${scoreBg} ${scoreColor}`
          }`}>
            {formatAnswerForPDF(criterion, item)}
          </span>
          {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </div>
      
      {/* Blocker banner */}
      {isBlocked && expanded && (
        <div className="bg-red-600 text-white text-xs font-bold px-5 py-2 flex items-center gap-2">
          <AlertTriangle size={12} />
          CRITERIO BLOQUEANTE — Se publicó con bloqueo
        </div>
      )}
      
      {/* Expanded content */}
      {expanded && (
        <div className="px-5 pb-4 bg-slate-50/50 space-y-3">
          {/* Focus/Description */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Enfoque del Criterio</p>
            <p className="text-sm text-slate-700 leading-relaxed">{criterion.focus}</p>
          </div>

          {/* Recinto Seguro - shown in blue when score is 10 */}
          {criterion.recintoSeguro && (
            <div className={`p-3 rounded-sm text-sm border-l-4 ${
              score === 10 
                ? 'bg-blue-50 border-blue-500 text-blue-800' 
                : 'bg-slate-50 border-slate-300 text-slate-600'
            }`}>
              <p className="font-semibold text-xs uppercase tracking-wide opacity-70 mb-1">
                {score === 10 ? '✓ Recinto Seguro:' : 'Recinto Seguro:'}
              </p>
              <p className="leading-relaxed">{criterion.recintoSeguro}</p>
            </div>
          )}
          
          {/* Score label */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Calificación:</span>
            <span className={`text-sm font-bold ${scoreColor}`}>
              {formatAnswerForPDF(criterion, item)}
            </span>
          </div>

          {/* Description of selected level */}
          {!item?.not_applicable &&
            criterion.question_type !== 'info' &&
            criterion.descriptions?.[score] && (
            <div className="p-3 rounded-sm text-sm bg-white border border-slate-200">
              <p className="font-semibold text-xs mb-1 uppercase tracking-wide text-slate-500">Descripción del nivel {score}:</p>
              <p className="leading-relaxed text-slate-700">{criterion.descriptions[score]}</p>
            </div>
          )}
          
          {/* Recommendation */}
          {hasRecommendation && (
            <div className={`p-3 rounded-sm text-sm border-l-4 ${
              score === 10 ? 'bg-blue-50 border-blue-500 text-blue-800' :
              score >= 8 ? 'bg-green-50 border-green-500 text-green-800' :
              score >= 4 ? 'bg-yellow-50 border-yellow-500 text-yellow-800' :
              'bg-red-50 border-red-500 text-red-800'
            }`}>
              <p className="font-semibold text-xs mb-1 uppercase tracking-wide opacity-70">Recomendación:</p>
              <p className="leading-relaxed">{hasRecommendation}</p>
            </div>
          )}
          
          {/* Notes */}
          {item?.notes && (
            <div className="bg-white rounded-sm p-3 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Notas del Evaluador</p>
              <p className="text-sm text-slate-700 italic">{item.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ClienteEvaluationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const evalRes = await evaluationsAPI.get(id);
        const ev = evalRes.data;
        setEvaluation(ev);
      } catch { toast.error('Error al cargar evaluación'); }
      finally { setLoading(false); }
    }
    load();
  }, [id]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;
  if (!evaluation) return <div className="text-center py-12 text-slate-400">Evaluación no encontrada.</div>;

  // #TemplateEvalRender — antes esta pantalla armaba secciones y criterios
  // buscando `criterio_id` en el catálogo legacy, lo que pintaba 0/10 en
  // TODOS los criterios de cualquier evaluación hecha con plantilla (esas
  // guardan `question_id` y dejan `criterio_id` null). getEvalSections usa
  // las secciones que ya manda el backend y solo cae al catálogo cuando no
  // vienen.
  const displaySections = getEvalSections(evaluation);
  const gf = evaluation.general_fields || {};
  // #Fase0 — contrato C4: en evaluaciones de plantilla blocked_items trae el
  // question_id (UUID) y el cliente leía identificadores crudos. Se traducen
  // al título con las secciones del GET (por criterion_id o question_id),
  // igual que el banner del líder; si no se resuelve, queda el id.
  const blockedTitleById = {};
  (evaluation.sections || []).forEach(sec => (sec.criteria || []).forEach(c => {
    const title = c.title || c.criterion_text;
    if (!title) return;
    if (c.criterion_id !== undefined && c.criterion_id !== null) blockedTitleById[c.criterion_id] = title;
    if (c.question_id !== undefined && c.question_id !== null) blockedTitleById[c.question_id] = title;
  }));
  const blockedLabels = (evaluation.blocked_items || []).map(id => blockedTitleById[id] || id);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cliente-eval-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/cliente')} className="soem-btn-secondary p-2" aria-label="Volver"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">{gf.instalacion || 'Evaluación'}</h1>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={displayStatus(evaluation)} />
            <span className="bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded font-mono">v{evaluation.version}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Score summary */}
        <div className="soem-card p-5 space-y-5">
          <div className="text-center">
            <ScoreSummary evaluation={evaluation} />
            <SemaforoBadge semaforo={evaluation.semaforo} percentage={evaluationPercentage(evaluation)} size="lg" />
            <p className="text-4xl font-black text-soem-navy mt-3">
              {evaluation.score_summary ? evaluation.score : '—'}
              <span className="text-slate-400 text-xl font-normal">
                /{evaluation.score_summary ? evaluation.max_score : '—'}
              </span>
            </p>
            <p className="text-slate-400 text-sm">

              {percentageLabel(evaluationPercentage(evaluation), 1)} del puntaje máximo
            </p>
          </div>

          <div className="space-y-3">
            <p className="soem-label">Por Sección</p>
            {displaySections.map((s) => (
              <div key={s.key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-bold text-slate-600">{s.name}</span>
                  <span className="text-slate-500">{percentageLabel(s.percentage, 0)}</span>
                </div>
                <ScoreBar score={s.score} max={s.max} percentage={s.percentage} />
              </div>
            ))}
          </div>

          <ReportDownload evaluation={evaluation} role="cliente" />

        </div>

        {/* Criteria */}
        <div className="lg:col-span-2 space-y-4">
          {/* Info */}
          <div className="soem-card p-5 grid grid-cols-2 gap-4 text-sm">
            {[
              ['Instalación', gf.instalacion], ['Ubicación', gf.ubicacion],
              ['Evaluador', gf.evaluador], ['Fecha', gf.fecha],
            ].map(([k, v]) => v ? (
              <div key={k}>
                <p className="soem-label">{k}</p>
                <p className="font-medium text-soem-navy">{v}</p>
              </div>
            ) : null)}
          </div>

          {evaluation.is_blocked && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-4 flex items-start gap-3" data-testid="blocked-notice">
              <AlertTriangle size={18} className="text-red-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-800 text-sm">Evaluación con criterios bloqueantes</p>
                <p className="text-red-700 text-xs mt-0.5">Criterios {blockedLabels.join(', ')} tienen calificación 0.</p>
              </div>
            </div>
          )}

          {displaySections.map((section) => (
            <div key={section.key} className="soem-card overflow-hidden">
              <div className="bg-soem-navy px-5 py-3 flex justify-between items-center">
                <span className="text-white font-bold text-sm">{section.name}</span>
                <span className="text-slate-300 text-xs font-mono">
                  {section.score}/{section.max} pts
                </span>
              </div>
              {section.rows.map((row) => (
                <CriterionRow key={row.key} criterion={row.criterion} item={row.item} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
